/**
 * One ICD element, as its reader needs it (issue #52).
 *
 * What it IS (message, port, interface, and for an interface the disciplines its
 * type files it under), what its type says about it (the values of the
 * attributes its type carries), and what it declares crossing - direction first,
 * because that is what both sides must agree about. A connector adds its pinout,
 * editable here.
 *
 * Everything else is the full editor's, which this links to by name.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'
import { ApiError, api } from '../../api'
import { findKind } from '../../schema'
import type { EntityRow, InterfaceFeature, InterfaceTypeRow } from '../../schema'
import { useSchema } from '../../state/AppData'
import { MONOSPACE } from '../../theme'
import PinoutEditor from './PinoutEditor'
import { tail } from './icd-model'

export default function IcdElementPane({
  identifier,
  types,
}: {
  identifier: string
  /** Every interface type, to say which disciplines this one is filed under. */
  types: InterfaceTypeRow[]
}) {
  const schema = useSchema()
  const kind = findKind(schema, 'interfaces')
  const [row, setRow] = useState<EntityRow | null>(null)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    setRow(null)
    setFailure('')
    api.get('interfaces', identifier)
      .then((found) => live && setRow(found))
      .catch((error: ApiError) => live && setFailure(error.message))
    return () => {
      live = false
    }
  }, [identifier])

  if (failure) return <Alert severity="error">{failure}</Alert>
  if (!row) return <CircularProgress size={24} />

  const record = row as unknown as Record<string, unknown>
  const sort = String(record.declared_as ?? '')
  const features = (record.features as InterfaceFeature[] | undefined) ?? []
  const statable = new Set((record.statable as string[] | undefined) ?? [])

  // The type it is built on, and the disciplines that type reaches.
  const typeTails = String(record.type_name ?? '').split(',').map((entry) => tail(entry.trim()))
  const itsTypes = types.filter((type) => typeTails.includes(type.name))
  const disciplines = [...new Set(itsTypes.flatMap((type) => type.disciplines))]

  // The attributes its type lets it state, with what it states for each.
  const values = (kind?.fields ?? [])
    .filter((field) => field.feature && statable.has(field.feature.split('.')[0]))
    .filter((field) => field.name !== 'publish_rate' || sort === 'message')
  const ends = features.filter((feature) => feature.is_end)
  const crossing = features.filter((feature) => !feature.is_end && !(feature.keyword === 'port' && tail(feature.type_name) === 'Pin'))

  return (
    <Stack spacing={1.5}>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">{row.name || row.id}</Typography>
          <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>{row.id}</Typography>
          <Chip size="small" variant="outlined" label={sort || 'element'} />
          {disciplines.map((discipline) => <Chip key={discipline} size="small" label={discipline} />)}
          <Box sx={{ flexGrow: 1 }} />
          <RouterLink to={`/interfaces/${encodeURIComponent(row.id)}`} style={{ fontSize: '0.8rem' }}>
            Open the full editor
          </RouterLink>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONOSPACE }}>
          {row.qualified_name}
          {record.type_name ? ` :> ${String(record.type_name)}` : ''}
        </Typography>
        {record.statement ? (
          <Typography variant="body2" sx={{ mt: 1 }}>{String(record.statement)}</Typography>
        ) : null}
      </Box>

      {values.length > 0 && (
        <>
          <Divider />
          <Typography variant="subtitle2">What its type states</Typography>
          <Table size="small" aria-label="Attribute values">
            <TableBody>
              {values.map((field) => (
                <TableRow key={field.name}>
                  <TableCell sx={{ width: '40%' }}>{field.label}</TableCell>
                  <TableCell sx={{ fontFamily: MONOSPACE }}>
                    {record[field.name] === null || record[field.name] === undefined || record[field.name] === ''
                      ? <Typography variant="body2" color="text.secondary">not stated</Typography>
                      : String(record[field.name])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {ends.length > 0 && (
        <>
          <Divider />
          <Typography variant="subtitle2">Ends</Typography>
          <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
            {ends.map((end) => (
              <Box key={end.name}>
                end {end.keyword} {end.name} : {end.is_conjugate ? '~' : ''}{end.type_name}
              </Box>
            ))}
          </Box>
        </>
      )}

      {crossing.length > 0 && (
        <>
          <Divider />
          <Typography variant="subtitle2">
            {sort === 'message' ? 'Fields' : 'What crosses it'}
          </Typography>
          <Table size="small" aria-label={sort === 'message' ? 'Message fields' : 'What crosses it'}>
            <TableHead>
              <TableRow>
                {sort !== 'message' && <TableCell>Direction</TableCell>}
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {crossing.map((feature) => (
                <TableRow key={feature.name}>
                  {sort !== 'message' && <TableCell>{feature.direction || '—'}</TableCell>}
                  <TableCell sx={{ fontFamily: MONOSPACE }}>{feature.name}</TableCell>
                  <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.8rem' }}>
                    {feature.is_conjugate ? '~' : ''}{feature.type_name}{feature.multiplicity}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {/* The editor draws its own divider, and nothing at all for a port def
          with no pins - which is every port that is not a connector. */}
      {sort === 'port' && <PinoutEditor identifier={row.id} />}
    </Stack>
  )
}
